import { useMemo } from "react";
import {
  useV2GetAccountQuery,
  useV2ListBusinessUnitsForAccountQuery,
  useV2ListPaymentTermsForAccountQuery,
  useV2ListPresalesEngineersForAccountQuery,
  useV2ListProjectVariablesQuery,
  useV2ListRateTablesForAccountQuery,
  useV2ShowCurrentUserForAccountQuery,
} from "@generated";
import type { SearchSelectOption } from "@components/FormsV2/SearchSelect";
import { formatDate } from "@utils/helperFunctions";
import type { ApiRecord } from "../types";

// The shape ProjectVariables.js renders and edits.
export interface FormVariable {
  name: string;
  label: string;
  "variable-type": string;
  "variable-context": string;
  required: boolean;
  minimum: number | null;
  maximum: number | null;
  select_options: { key?: string; value?: string; default?: boolean }[];
  value: string;
}

export interface FieldLabels {
  client: string;
  sales_executive: string;
  presales_engineer: string;
  business_unit: string;
  location: string;
  crm_opportunity: string;
}

export const DEFAULT_FIELD_LABELS: FieldLabels = {
  client: "Client",
  sales_executive: "Sales Executive",
  presales_engineer: "Pre-Sales Engineer",
  business_unit: "Business Unit",
  location: "Service Location",
  crm_opportunity: "CRM Opportunity",
};

const toOptions = (records?: ApiRecord[]): SearchSelectOption[] =>
  (records ?? []).map((r) => ({
    value: String(r.id),
    label: r.attributes?.name ?? "",
  }));

// The API dasherizes these keys (the generated type shows them underscored).
// A required date starts on today; msa_date is covered by the MSA Date field.
const toFormVariables = (records?: ApiRecord[]): FormVariable[] =>
  (records ?? [])
    .map((r) => r.attributes ?? {})
    .filter((a) => a.name !== "msa_date")
    .map((a) => ({
      name: a.name,
      label: a.label,
      "variable-type": a["variable-type"],
      "variable-context": a["variable-context"],
      required: a.required === true,
      minimum: a.minimum ?? null,
      maximum: a.maximum ?? null,
      select_options: a["select-options"] ?? [],
      value:
        a.required === true && a["variable-type"] === "date"
          ? formatDate(new Date())
          : "",
    }));

/** Option lists, account settings and defaults for the create-project form. */
export const useProjectFormOptions = () => {
  const account = useV2GetAccountQuery({});
  const currentUser = useV2ShowCurrentUserForAccountQuery({});
  const presales = useV2ListPresalesEngineersForAccountQuery({
    page: { size: 500 },
  });
  const paymentTerms = useV2ListPaymentTermsForAccountQuery({});
  const rateTables = useV2ListRateTablesForAccountQuery({
    page: { size: 500 },
  });
  const businessUnits = useV2ListBusinessUnitsForAccountQuery({});
  const projectVariables = useV2ListProjectVariablesQuery({
    filter: { "variable-context": "project" },
  });
  const locationVariables = useV2ListProjectVariablesQuery({
    filter: { "variable-context": "service_location" },
  });

  const queries = [
    account,
    currentUser,
    presales,
    paymentTerms,
    rateTables,
    businessUnits,
    projectVariables,
    locationVariables,
  ];
  const isLoading = queries.some((q) => q.isLoading);

  return useMemo(() => {
    const accountAttributes: any = account.data?.data?.attributes ?? {};
    // Documented as a list; the endpoint returns the one user.
    const userData: any = currentUser.data?.data;
    const user = Array.isArray(userData) ? userData[0] : userData;
    const preferredRateTable = user?.attributes?.["preferred-rate-table"]?.name;

    const activeRateTables = (
      (rateTables.data?.data ?? []) as ApiRecord[]
    ).filter((t) => t.attributes?.active === true);
    const terms = (paymentTerms.data?.data ?? []) as ApiRecord[];
    const units = (businessUnits.data?.data ?? []) as ApiRecord[];

    return {
      isLoading,
      isCrm: accountAttributes["crm?"] === true,
      fieldLabels: {
        ...DEFAULT_FIELD_LABELS,
        ...accountAttributes["field-labels"],
      } as FieldLabels,
      presalesOptions: toOptions(presales.data?.data as ApiRecord[]),
      paymentTermOptions: toOptions(terms),
      rateTableOptions: toOptions(activeRateTables),
      businessUnitOptions: toOptions(units),
      projectVariables: toFormVariables(
        projectVariables.data?.data as ApiRecord[]
      ),
      locationVariables: toFormVariables(
        locationVariables.data?.data as ApiRecord[]
      ),
      // Payment term and rate table always start on a value, as the native
      // selects they replaced did: the default / preferred one, else the first.
      defaults: {
        paymentTermId:
          (terms.find((t) => t.attributes?.default === true) ?? terms[0])?.id ??
          null,
        rateTableId:
          (
            activeRateTables.find(
              (t) => t.attributes?.name === preferredRateTable
            ) ?? activeRateTables[0]
          )?.id ?? null,
        businessUnitId: units.length === 1 ? String(units[0].id) : null,
      },
    };
  }, [
    isLoading,
    account.data,
    currentUser.data,
    presales.data,
    paymentTerms.data,
    rateTables.data,
    businessUnits.data,
    projectVariables.data,
    locationVariables.data,
  ]);
};

export default useProjectFormOptions;
